"use strict";
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTuaStatus = exports.useGlobalPointerDownOutsideListener = exports.useResizeObserver = exports.useEffectExceptOnMount = exports.AxeOptOut = exports.EventUtilsOptOut = exports.EventUtilsOptOutAttr = exports.getComponentName = exports.assignRef = exports.mergeRefs = exports.mergeEventHandlers = exports.convertToStatefulComponentClass = void 0;
var tslib_1 = require("tslib");
var React = tslib_1.__importStar(require("react"));
var GlobalPointerDownOutsideListener_1 = require("./GlobalPointerDownOutsideListener");
function isSFC(type) {
    var _a;
    return !((_a = type.prototype) === null || _a === void 0 ? void 0 : _a.render);
}
function convertToStatefulComponentClass(componentType) {
    var _a;
    if (isSFC(componentType)) {
        var statelessComponent_1 = componentType;
        return _a = /** @class */ (function (_super) {
                tslib_1.__extends(StatefulSFCWrapper, _super);
                function StatefulSFCWrapper() {
                    return _super !== null && _super.apply(this, arguments) || this;
                }
                StatefulSFCWrapper.prototype.render = function () {
                    return statelessComponent_1(this.props);
                };
                return StatefulSFCWrapper;
            }(React.Component)),
            _a.displayName = getComponentName(statelessComponent_1),
            _a;
    }
    else {
        return componentType;
    }
}
exports.convertToStatefulComponentClass = convertToStatefulComponentClass;
function mergeEventHandlers(h1, h2) {
    return (h1 && h2 && function (e) { h1(e); h2(e); })
        || h1 || h2;
}
exports.mergeEventHandlers = mergeEventHandlers;
function isCallbackRef(ref) {
    return typeof ref === 'function';
}
var mergeRefs = function () {
    var refs = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        refs[_i] = arguments[_i];
    }
    return function (value) {
        refs.forEach(function (resolvableRef) { return assignRef(resolvableRef, value); });
    };
};
exports.mergeRefs = mergeRefs;
function assignRef(ref, value) {
    if (isCallbackRef(ref)) {
        ref(value);
    }
    else if (ref) {
        ref.current = value;
    }
}
exports.assignRef = assignRef;
function getComponentName(componentType) {
    return componentType.displayName || componentType.name || 'AnonymousComponent';
}
exports.getComponentName = getComponentName;
/** Adding this prop tells VizClient's event-utils to completely ignore the element */
exports.EventUtilsOptOutAttr = 'data-eventutils-optout';
exports.EventUtilsOptOut = Object.freeze((_a = {},
    _a[exports.EventUtilsOptOutAttr] = true,
    _a));
/** Adding this prop allows us to ignore certain "errors" flagged by the axe accessibility testing framework. TFSID 1193920 */
exports.AxeOptOut = Object.freeze({
    'data-axe-ignore': true,
});
/** Identical to React.useEffect, except that it never runs on mount. */
var useEffectExceptOnMount = function (effect, dependencies) {
    var mounted = React.useRef(false);
    React.useEffect(function () {
        if (mounted.current) {
            return effect();
        }
        else {
            mounted.current = true;
            return undefined;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies);
    // Reset on unmount for the next mount. (Relevant in React 18+)
    React.useEffect(function () {
        return function () { mounted.current = false; };
    }, []);
};
exports.useEffectExceptOnMount = useEffectExceptOnMount;
/**
 * Wrapper around React hook setup for using `ResizeObserver` to respond to resize of an element.
 * @param callback called upon resize of `element`
 * @param element `HTMLElement` to observe with the `ResizeObserver`
 * @param startObserving optional flag to control when observer should start and stop
 */
var useResizeObserver = function (element, callback, startObserving) {
    if (startObserving === void 0) { startObserving = true; }
    var resizeObserverRef = React.useRef();
    var onResizeCallback = React.useCallback(callback, [callback]);
    var observe = React.useCallback(function () {
        var _a;
        if (element && !resizeObserverRef.current) {
            resizeObserverRef.current = new ResizeObserver(onResizeCallback);
        }
        if (element) {
            (_a = resizeObserverRef.current) === null || _a === void 0 ? void 0 : _a.observe(element);
        }
    }, [element, onResizeCallback]);
    var stopObserving = React.useCallback(function () {
        if (element && resizeObserverRef.current) {
            resizeObserverRef.current.unobserve(element);
            resizeObserverRef.current = undefined;
        }
    }, [element]);
    React.useEffect(function () {
        if (element && startObserving) {
            observe();
        }
        return stopObserving;
    }, [element, startObserving, observe, stopObserving]);
};
exports.useResizeObserver = useResizeObserver;
/**
 * Wrapper around React hook setup for using `GlobalPointerDownOutsideListener` `subscribe` and `unsubscribe`,
 * for e.g. to handle closing a popout element on click outside of the trigger elemnent.
 * @param triggerElement Element that triggers the popout
 * @param popoutElement Element popped out
 * @param handler triggered by pointer-down event
 */
var useGlobalPointerDownOutsideListener = function (triggerElement, popoutElement, handler) {
    var handlerCallback = React.useCallback(handler, [handler]);
    React.useEffect(function () {
        if (popoutElement && triggerElement) {
            GlobalPointerDownOutsideListener_1.GlobalPointerDownOutsideListener.subscribe(triggerElement, handlerCallback);
        }
        return function () { return GlobalPointerDownOutsideListener_1.GlobalPointerDownOutsideListener.unsubscribe(triggerElement, handlerCallback); };
    }, [popoutElement, triggerElement, handlerCallback]);
};
exports.useGlobalPointerDownOutsideListener = useGlobalPointerDownOutsideListener;
/** @returns if we're running in a Tableau Unified Analytics embed, i.e. if the `body` element has class `tua-embed` */
function useTuaStatus() {
    var readTua = function () { return document.body.classList.contains('tua-embed'); };
    var _a = React.useState(readTua()), isTua = _a[0], setTua = _a[1];
    React.useEffect(function () {
        var observer = new MutationObserver(function () { return setTua(readTua()); });
        observer.observe(document.body, { attributeFilter: ['class'] });
        return function () { return observer.disconnect(); };
    }, []);
    return isTua;
}
exports.useTuaStatus = useTuaStatus;
//# sourceMappingURL=ReactHelpers.js.map